import { createContext, ReactNode, useContext, useState } from 'react';
import { MoveDown, MoveDownLeft, MoveDownRight, MoveLeft, MoveRight, MoveUp, MoveUpLeft, MoveUpRight } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { Button } from '../Button';

type Direction = 'n' | 'nw' | 'ne' | 's' | 'se' | 'sw' | 'e' | 'w' | 'center';

interface PositionPickerProps {
	renderDirections?: Direction[];
	defaultValue?: string | 'center';
	value?: string;
	onSelect?: (e: string) => void;
}

interface PickerContextType {
	activeButtons: Set<string>;
	toggleButton: (direction: string) => void;
}

const ROWS: Direction[][] = [
	['nw', 'n', 'ne'],
	['w', 'center', 'e'],
	['sw', 's', 'se'],
];

const ICONS: Record<Direction, ReactNode> = {
	nw: <MoveUpLeft />,
	n: <MoveUp />,
	ne: <MoveUpRight />,
	w: <MoveLeft />,
	center: <span className='block size-1.5 rounded-full bg-current' />,
	e: <MoveRight />,
	sw: <MoveDownLeft />,
	s: <MoveDown />,
	se: <MoveDownRight />,
};

const directionMap: Record<string, string> = {
	n: 'top-center',
	ne: 'top-right',
	nw: 'top-left',
	s: 'bottom-center',
	se: 'bottom-right',
	sw: 'bottom-left',
	e: 'middle-right',
	w: 'middle-left',
	center: 'center',
};

const reverseDirectionMap: Record<string, string> = Object.fromEntries(Object.entries(directionMap).map(([k, v]) => [v, k]));

const PickerContext = createContext<PickerContextType | undefined>(undefined);

export const PickerProvider = ({ children, defaultValue, value, onSelect }: { children: ReactNode; defaultValue: string; value?: string; onSelect?: (e: string) => void }) => {
	const [activeButtons, setActiveButtons] = useState<Set<string>>(new Set([defaultValue]));

	const valueDirection = value ? reverseDirectionMap[value] : undefined;
	const currentActive = valueDirection ? new Set([valueDirection]) : activeButtons;

	const toggleButton = (direction: string) => {
		const translatedDirection = directionMap[direction] || direction;

		onSelect?.(translatedDirection);

		if (value !== undefined) return;

		setActiveButtons(() => {
			const newSet: Set<string> = new Set();
			newSet.add(direction);
			return newSet;
		});
	};

	return (
		<PickerContext.Provider
			value={{
				activeButtons: currentActive,
				toggleButton,
			}}>
			{children}
		</PickerContext.Provider>
	);
};

const usePickerContext = () => {
	const context = useContext(PickerContext);
	if (!context) {
		throw new Error('usePickerContext must be used within a PickerProvider');
	}
	return context;
};

const Cell = ({ direction }: { direction: Direction }) => {
	const { activeButtons, toggleButton } = usePickerContext();
	const isActive = activeButtons.has(direction);

	return (
		<Button
			type='button'
			id={direction}
			variant={isActive ? 'default' : 'outline'}
			className={twMerge(
				'flex h-[30px] min-h-[30px] flex-[1_0_0] items-center justify-center gap-2 rounded-lg border px-2 py-1 active:scale-100',
				isActive && 'border-gray-900',
			)}
			onClick={() => toggleButton(direction)}>
			{ICONS[direction]}
		</Button>
	);
};

const PositionPicker = ({ defaultValue = 'center', value, renderDirections = ['n', 'ne', 'nw', 's', 'se', 'sw', 'e', 'w', 'center'], onSelect }: PositionPickerProps) => {
	const colunas = [0, 1, 2].filter((col) => ROWS.some((row) => renderDirections.includes(row[col])));

	return (
		<PickerProvider defaultValue={defaultValue} value={value} onSelect={onSelect}>
			<div className='flex w-full flex-col gap-2'>
				{ROWS.map((row) => {
					if (!row.some((direction) => renderDirections.includes(direction))) return null;

					return (
						<div key={row.join('-')} className='flex gap-2'>
							{colunas.map((col) => {
								const direction = row[col];

								return renderDirections.includes(direction) ? (
									<Cell key={direction} direction={direction} />
								) : (
									<div key={direction} className='invisible h-[30px] min-h-[30px] flex-[1_0_0]' />
								);
							})}
						</div>
					);
				})}
			</div>
		</PickerProvider>
	);
};

export default PositionPicker;
